import { SearchResponse, SearchResponseStatus, SearchType } from 'fym-common/src/types/searchService';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { SearchOptions } from '../../../redux/api/musicAPI.types';

/** At most this many results show (design d4). */
export const FIND_IT_MAX_RESULTS = 10;

/** The search waits this long after the last key press (design d4: 400 ms). */
export const FIND_IT_DEBOUNCE_MS = 400;

/** One result of "Find it": a track of the one service. */
export interface FindItResult {
  key: string;
  title: string;
  /** "Artist · Album", what the search answer gives. */
  artistNames: string;
  albumName: string | null;
  imageUrl: string | null;
  /** The address that "Use this" saves as the link of the service. */
  url: string;
}

/** The text of the field when the sheet opens: "<artist> <title>" (design d4). */
export function findItPrefill(title: string, artistName: string): string {
  return `${artistName} ${title}`.trim();
}

/**
 * The body of the MusicAPI search (the search of the claim flow, `POST /search`) for one service,
 * tracks only. The prefilled text searches with the title and the artist apart, which matches
 * best; a text that the user changed goes as the track words.
 */
export function findItSearchOptions(
  integrationType: MusicAPIIntegrationID,
  query: string,
  song: { title: string; artistName: string }
): SearchOptions {
  const trimmed = query.trim();
  const isPrefill = trimmed === findItPrefill(song.title, song.artistName);
  return {
    type: SearchType.track,
    sources: [integrationType],
    track: isPrefill ? song.title : trimmed,
    ...(isPrefill && song.artistName.trim() !== '' ? { artist: song.artistName } : {}),
  };
}

/** The tracks of the answer for this service that have an address, at most 10. */
export function findItResults(response: SearchResponse | undefined, integrationType: string): FindItResult[] {
  const results: FindItResult[] = [];
  (response?.tracks ?? []).forEach((result, index) => {
    const track = result.data;
    if (
      result.status !== SearchResponseStatus.success ||
      track === null ||
      track.url === null ||
      track.url === '' ||
      (result.source !== undefined && result.source !== integrationType)
    ) {
      return;
    }
    results.push({
      key: `${track.externalId ?? track.url}-${index}`,
      title: track.name,
      artistNames: (track.artistNames ?? []).join(', '),
      albumName: track.albumName,
      imageUrl: track.imageUrl,
      url: track.url,
    });
  });
  return results.slice(0, FIND_IT_MAX_RESULTS);
}
